import { withFormik, Form, Field } from "formik";
import * as Yup from "yup";
import { FormGroup, Label, Button } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import axios from "../../services/api/api";
import Select from "react-select";
import { AUTH_BASE_URL } from "../../constants/api.constants";
import { checkNameLengthAndCharacters } from "../../Utilities/checkerFunctions";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";
import { compareStrings } from "../../Utilities/sortHelper";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import TeacherPermissionFields from "../Teacher/TeacherPermissionFields";

const CreateTeacherForm = ({
  values,
  errors,
  touched,
  isSubmitting,
  dismissModal,
  setFieldValue,
}) => {
  const intl = useIntl();
  const handleChange = (e) => {
    values.selectedClasses = e.map((t) => t.value);
  };

  const getTeacherClasses = () => {
    var classes = [];
    values.teacherToUpdate.classes.forEach((c) => {
      classes.push({
        value: c.id,
        label: c.name,
        clearableValue: true,
      });
    });
    return classes;
  };

  return (
    <div className="col-12 col-sm-12">
      <Form>
        <FormGroup>
          <Label>
            <FormattedMessage id="Name" />
          </Label>
          <Field
            className={`form-control ${
              errors.name && touched.name && "is-invalid"
            }`}
            type="input"
            name="name"
            placeholder={intl.formatMessage({ id: "TeacherName" })}
          />
          {errors.name && touched.name && (
            <div className="invalid-feedback">{errors.name}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label>
            <FormattedMessage id="EmailAddress" />
          </Label>
          <Field
            className={`form-control ${
              errors.email && touched.email && "is-invalid"
            }`}
            type="input"
            name="email"
            placeholder={intl.formatMessage({ id: "EmailAddress" })}
          />
          {errors.email && touched.email && (
            <div className="invalid-feedback">{errors.email}</div>
          )}
        </FormGroup>

        <FormGroup className={values.simpleCreate ? "d-none" : ""}>
          <TeacherPermissionFields
            values={values}
            grantor={{ fullAccess: true, purchaser: true }}
            onToggle={setFieldValue}
          />
        </FormGroup>

        <FormGroup className={values.simpleCreate ? "d-none" : ""}>
          <Label for="exampleSelectMulti">
            <FormattedMessage id="ChooseClassOrClasses" />
          </Label>
          <div
            className={
              errors.selectedClasses && touched.selectedClasses && "is-invalid"
            }
          >
            <Select
              placeholder={intl.formatMessage({ id: "ChooseClasses" })}
              isMulti
              noOptionsMessage={() =>
                intl.formatMessage({ id: "NoMoreSelectableClassesForTeacher" })
              }
              onChange={(e) => handleChange(e)}
              defaultValue={
                values.simpleCreate
                  ? values.schoolClasses[0]
                  : values.teacherToUpdate
                    ? getTeacherClasses()
                    : null
              }
              options={values.schoolClasses}
            />
          </div>
          {errors.selectedClasses && touched.selectedClasses && (
            <div className="invalid-feedback">{errors.selectedClasses}</div>
          )}
        </FormGroup>
        <div className="d-flex justify-content-end gap-2">
          <Button
            color="secondary"
            outline
            type="button"
            onClick={dismissModal}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button color="primary" outline type="submit" disabled={isSubmitting}>
            <FormattedMessage id={isSubmitting ? "Wait" : "Save"} />
          </Button>
        </div>
      </Form>
    </div>
  );
};

const FormikForm = withFormik({
  mapPropsToValues({
    name,
    schoolClasses,
    email,
    teacherToUpdate,
    simpleCreate,
  }) {
    return {
      teacherToUpdate: teacherToUpdate,
      classObjects: schoolClasses,
      schoolClasses: schoolClasses
        .map((sc) => {
          return {
            value: sc.id,
            label: sc.name,
            clearableValue: true,
          };
        })
        .sort((a, b) => compareStrings(a.label, b.label)),
      name: teacherToUpdate ? teacherToUpdate.name : name || "",
      selectedClasses:
        teacherToUpdate && teacherToUpdate.classes
          ? teacherToUpdate.classes.map((c) => c.id)
          : [],
      email: teacherToUpdate ? teacherToUpdate.email : email || "",
      fullAccess: teacherToUpdate ? teacherToUpdate.fullAccess : false,
      purchaser: teacherToUpdate ? teacherToUpdate.purchaser : false,
      oldEmail: teacherToUpdate ? teacherToUpdate.email : email || "",
      simpleCreate: simpleCreate !== null ? simpleCreate : false,
    };
  },

  validationSchema: Yup.object().shape({
    name: Yup.string().required(
      <FormattedMessage id="Views.Admin.CreateTeacherForm.Error.RequireName" />,
    ),
    email: Yup.string()
      .email()
      .required(
        <FormattedMessage id="Views.Admin.CreateTeacherForm.Error.RequireEmail" />,
      ),
    selectedClasses: Yup.array().min(
      1,
      <FormattedMessage id="Views.Admin.CreateTeacherForm.Error.SelectClass" />,
    ),
  }),

  handleSubmit(values, { props, setErrors, resetForm, setSubmitting }) {
    if (props.teacherToUpdate) {
      this.updateExistingTeacher(values, props, setErrors, setSubmitting);
    } else {
      this.createNewTeacher(values, props, resetForm, setSubmitting);
    }
  },

  updateUser(values, setSubmitting, props) {
    if (checkNameLengthAndCharacters(values.name) !== true) {
      toast.warn(checkNameLengthAndCharacters(values.name));
      setSubmitting(false);
    } else {
      axios
        .put("/user/UpdateUser", {
          name: values.name,
          oldEmail: values.oldEmail,
          newEmail: values.email,
        })
        .then(() => {
          this.updateTeacherWithClasses(values, setSubmitting, props);
        })
        .catch((err) => {
          toast.warn(
            formatServiceError(
              props.intl,
              err?.response?.data,
              "UserUpdateFailed",
            ),
          );
          setSubmitting(false);
        });
    }
  },

  updateTeacherWithClasses(values, setSubmitting, props) {
    axios
      .post("/teacher/updateteacher", {
        id: values.teacherToUpdate.id,
        classes: values.selectedClasses.map((c) => {
          return values.classObjects.find((co) => co.id === c);
        }),
        fullAccess: values.fullAccess,
        purchaser: values.purchaser,
      })
      .then(() => {
        toast.success(<FormattedMessage id="UserUpdated" />);
        setSubmitting(false);
        values.teacherToUpdate = null;
        props.loadTeachers();
        props.dismissModal();
      })
      .catch((err) => {
        toast.error(
          formatServiceError(props.intl, err?.response?.data),
          TOAST_CONFIG,
        );
        setSubmitting(false);
      });
  },

  updateExistingTeacher(values, props, setErrors, setSubmitting) {
    if (values.oldEmail !== values.email) {
      axios
        .post(
          `${AUTH_BASE_URL}/token/IsEmailTaken/?email=${encodeURIComponent(
            values.email,
          )}`,
        )
        .then((response) => {
          if (response.data === false) {
            this.updateUser(values, setSubmitting, props);
          } else {
            setErrors({ email: <FormattedMessage id="EmailAddressTaken" /> });
            setSubmitting(false);
          }
        })
        .catch((err) => {
          setErrors({
            email: formatServiceError(props.intl, err?.response?.data),
          });
          setSubmitting(false);
        });
    } else {
      this.updateUser(values, setSubmitting, props);
    }
  },

  createNewTeacher(values, props, resetForm, setSubmitting) {
    if (checkNameLengthAndCharacters(values.name) !== true) {
      toast.warn(checkNameLengthAndCharacters(values.name));
      setSubmitting(false);
    } else {
      axios
        .post(
          `/teacher?classIds=${values.selectedClasses.join("&classIds=")}`,
          {
            name: values.name,
            email: values.email,
            fullAccess: values.fullAccess,
            purchaser: values.purchaser,
          },
        )
        .then(() => {
          toast.success(
            <FormattedMessage id="Views.Admin.CreateTeacherForm.TeacherCreated" />,
            TOAST_CONFIG,
          );
          props.loadTeachers();
          resetForm();
          props.dismissModal();
        })
        .catch((err) => {
          toast.error(
            formatServiceError(props.intl, err?.response?.data),
            TOAST_CONFIG,
          );
        })
        .finally(() => setSubmitting(false));
    }
  },
})(CreateTeacherForm);

const CreateTeacherFormWithIntl = (props) => {
  const intl = useIntl();

  return <FormikForm {...props} intl={intl} />;
};

export default CreateTeacherFormWithIntl;
